import { DragEndEvent } from "@dnd-kit/core";
import { useTransition } from "react";
import { SortableListHolder } from "../../controls";
import { getFromDataPath } from "../../utility";
import { ExpressionLogicalOperatorEnum, SubClause } from "../ars_ldm";
import { useArsContext } from "../contexts";
import {
  ADD_EXTRA_WHERE_CLAUSE,
  ADD_NOT_TO_WHERE_CLAUSE,
  PATCH_ARS_STATE,
  REMOVE_NOT_FROM_WHERE_CLAUSE,
} from "../contexts/interface";
import {
  isSubClauseWithReference,
  isWhereClause,
  isWhereClauseWithCompoundExpression,
  isWhereClauseWithCondition,
} from "../typeGuards";
import { AddWhereClauseButton } from "./AddWhereClauseButton";
import { SubClauseItemControl } from "./SubClauseItemControl";
import { WhereClauseConditionItemControl } from "./WhereClauseConditionItemControl";

/**
 * Props for the WhereClauseEditor component.
 */
export interface WhereClauseEditorProps {
  /** RFC 6901 JSON Pointer path to the entity (AnalysisSet, DataSubset, or Group) in ArsContext state */
  dataPath: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Expanded editor interface for where clause conditions.
 *
 * This component displays the detailed controls for editing where clauses:
 * - Single condition: Displays NOT checkbox, one condition editor with an "Add Clause" button
 * - Compound expression with 1 clause: Displays NOT checkbox and the single clause editor
 * - Compound expression with 2+ clauses: Displays logical operator selector (AND/OR), list of clauses
 *   with drag-drop reordering, and remove buttons for each clause
 *
 * The component handles:
 * - Converting single condition to compound expression (ADD_EXTRA_WHERE_CLAUSE)
 * - Removing clauses and reverting to single condition when appropriate (REMOVE_WHERE_CLAUSE)
 * - Drag-drop reordering of clauses (updates level and order properties)
 * - Logical operator selection
 * - Adding and removing NOT operators
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the entity in ArsContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the where clause editor.
 */
export const WhereClauseEditor = ({
  dataPath,
  disabled = false,
}: WhereClauseEditorProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const [isPending, startTransition] = useTransition();

  // Get the current entity from state
  const whereClause = getFromDataPath(dataPath, state, isWhereClause);
  const condition = isWhereClauseWithCondition(whereClause) ? whereClause.condition : undefined;
  const ce = isWhereClauseWithCompoundExpression(whereClause)
    ? whereClause.compoundExpression
    : undefined;
  const hasCondition = condition !== undefined;
  const whereClauseCount = hasCondition ? 1 : (ce?.whereClauses?.length ?? 0);
  const refClauseCount =
    ce?.whereClauses?.filter((clause) => isSubClauseWithReference(clause)).length ?? 0;

  /**
   * Handles NOT checkbox change.
   * Dispatches ADD_NOT_TO_WHERE_CLAUSE when checked, or REMOVE_NOT_FROM_WHERE_CLAUSE when unchecked.
   *
   * @param event - The checkbox change event.
   */
  const handleNotCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    startTransition(() => {
      if (event.target.checked) {
        dispatch({
          operation: ADD_NOT_TO_WHERE_CLAUSE,
          dataPath,
        });
      } else {
        dispatch({
          operation: REMOVE_NOT_FROM_WHERE_CLAUSE,
          dataPath,
        });
      }
    });
  };

  /**
   * Handles adding a new where clause.
   * Converts single condition to compound expression if needed.
   */
  const handleAddClause = (): void => {
    startTransition(() => {
      dispatch({
        operation: ADD_EXTRA_WHERE_CLAUSE,
        dataPath,
        newLength: whereClauseCount + 1,
      });
    });
  };

  /**
   * Handles drag-end event for reordering clauses.
   *
   * @param event - The drag end event from dnd-kit.
   */
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;

    /* istanbul ignore else */
    if (over !== null && over.id !== active.id && ce) {
      const whereClauses =
        ce.whereClauses ??
        // istanbul ignore next
        [];

      const oldIndex = whereClauses.findIndex((clause) => clause.id === active.id);
      const newIndex = whereClauses.findIndex((clause) => clause.id === over.id);

      // Reorder the array
      const reorderedClauses = [...whereClauses];
      const [movedClause] = reorderedClauses.splice(oldIndex, 1);
      reorderedClauses.splice(newIndex, 0, movedClause);

      // Update order properties
      const updatedClauses = reorderedClauses.map((clause, index) => ({
        ...clause,
        order: index,
      }));

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/compoundExpression/whereClauses`,
        value: updatedClauses,
      });
    }
  };

  return !whereClause ? (
    <span className="text-danger">Entity not found at path: {dataPath}</span>
  ) : (
    <div>
      {whereClauseCount <= 1 ? (
        <div
          className="d-flex align-items-center"
          style={{ height: "32px" }}
        >
          <label
            className="form-check-label"
            htmlFor={`${dataPath.replace(/\//g, "-")}-not-checkbox`}
          >
            NOT
          </label>
          <input
            type="checkbox"
            className="form-check-input ms-2"
            id={`${dataPath.replace(/\//g, "-")}-not-checkbox`}
            checked={ce?.logicalOperator === "NOT"}
            disabled={disabled}
            onChange={handleNotCheckboxChange}
          />
        </div>
      ) : (
        <div
          className="d-flex align-items-center"
          style={{ height: "32px" }}
        >
          <select
            className="form-select form-select-sm w-auto"
            id={`${dataPath.replace(/\//g, "-")}-logical-operator-select`}
            disabled={disabled}
            onChange={(ret) => {
              dispatch({
                operation: PATCH_ARS_STATE,
                dataPath: `${dataPath}/compoundExpression/logicalOperator`,
                value: ret.currentTarget.value as ExpressionLogicalOperatorEnum,
              });
            }}
          >
            <option value={ExpressionLogicalOperatorEnum.AND}>AND</option>
            <option value={ExpressionLogicalOperatorEnum.OR}>OR</option>
          </select>
        </div>
      )}
      <SortableListHolder
        canMove={!disabled}
        handleDragEnd={handleDragEnd}
        showBorder={false}
        items={
          hasCondition
            ? [
                {
                  id: "single-condition",
                  element: (
                    <WhereClauseConditionItemControl
                      dataPath={`${dataPath}/condition`}
                      disabled={disabled}
                    />
                  ),
                },
              ]
            : (
                ce?.whereClauses ??
                // istanbul ignore next
                []
              ).map((clause: SubClause, ix: number) => ({
                id: clause.id,
                element: (
                  <SubClauseItemControl
                    isRemovable={whereClauseCount - refClauseCount > 1}
                    dataPath={`${dataPath}/compoundExpression/whereClauses/${ix}`}
                    disabled={disabled}
                  />
                ),
              }))
        }
      />
      <AddWhereClauseButton
        id={`${dataPath.replace(/\//g, "-")}-add-clause-button`}
        onClick={handleAddClause}
        isPending={isPending}
        disabled={disabled}
      />
    </div>
  );
};

WhereClauseEditor.displayName = "WhereClauseEditor";
